<template>
  <div v-if="model" data-test="group-member-table">
    <div style="height: 20px"></div>
    <div class="table-responsive text-big table-padding">
      <table
        id="watchTable"
        class="table table-hover"
        v-data-table="
          model.currentMembers.length && {
            paging: false,
            searching: false,
            info: false,
            columnDefs: [{ targets: 0, orderable: false }],
            order: [
              [2, 'asc'],
              [1, 'asc'],
            ],
          }
        "
      >
        <thead>
          <tr>
            <th style="max-width: 0"></th>
            <th></th>
            <th>Name</th>
            <th>Solo</th>
            <th>Viewpoints</th>
            <th>Develop</th>
            <th>Team Pathway</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="model.currentMembers.length === 0 && model.pendingMembers.length === 0">
            <td colspan="100" data-test="no-members-message">No members</td>
          </tr>
          <tr
            v-for="member in model.currentMembers"
            :key="member.userDetails.email"
            class="expandable-row"
            :class="{ expanded: isExpanded(member) }"
            data-test="group-member-row"
            style="position: relative"
          >
            <td v-if="member.isTeamOrOrgAdmin" style="max-width: 0">
              <Tooltip tag="div" text="Team lead" :key="member.userDetails.id">
                <img
                  class="leader-icon"
                  src="../../assets/leader-crown-icon.png"
                  alt="team lead or organisation admin"
                  tabindex="0"
                  data-test="group-member-admin-icon"
                />
              </Tooltip>
            </td>
            <td v-else></td>
            <td>
              <button
                type="button"
                class="showHideButton btn btn-default btn-xs"
                style="margin: -1px 0"
                aria-label="open more details"
                @click="toggleMemberExpansion(member)"
                data-test="expand-button"
              >
                <span v-if="!isExpanded(member)" class="glyphicon glyphicon-plus text-muted"></span>
                <span v-else class="glyphicon glyphicon-minus text-muted"></span>
              </button>
            </td>
            <td>
              <a
                v-if="memberProfileCanBeViewed(member)"
                :href="`/user/profile/${member.userDetails.id}`"
                data-test="member-name-link"
                >{{ memberFullName(member) }}</a
              >
              <span v-else data-test="member-name-label">{{ memberFullName(member) }}</span>
              <div style="min-width: 150px" class="hidden-on-collapse">
                <div style="height: 5px"></div>
                <div v-if="member.userDetails.email" style="font-weight: bold">
                  <a
                    :href="'mailto:' + member.userDetails.email"
                    target="_blank"
                    rel="noopener noreferrer"
                    >{{ member.userDetails.email }}</a
                  >
                </div>
              </div>
            </td>
            <td>
              <span class="hidden">{{ member.solo.linkSortPrefix }}</span>
              <TeamMemberReportLink :link="member.solo.activeReport" />
              <div
                v-for="(report, index) in member.solo.otherReports"
                :key="index"
                class="hidden-on-collapse"
              >
                <TeamMemberReportLink :link="report" />
              </div>
            </td>

            <td :data-sort="member.viewpoints.subReports.length" data-test="viewpoints-links">
              <TeamMemberReportLink :link="member.viewpoints.activeReport" />
              <div
                v-for="(respondent, index) in member.viewpoints.subReports"
                :key="index"
                class="hidden-on-collapse"
              >
                <TeamMemberReportLink :link="respondent" />
              </div>
            </td>

            <td :data-sort="developDay(member)">
              <template
                v-if="
                  member.develop &&
                  member.develop.currentStrengthProgress &&
                  member.develop.currentStrengthProgress.strengthItemId
                "
              >
                <TeamMemberDevelopLine :model="member.develop.currentStrengthProgress" />
              </template>
              <span v-else class="text-muted">no develop</span>
              <TeamMemberDevelopLine
                v-for="(otherProgress, index) in member.develop.otherProgress"
                :key="index"
                :model="otherProgress"
                class="hidden-on-collapse"
              />
            </td>
            <td>
              <template
                v-if="pathwayProgress(member.pathways.currentPathwaysInProgress, true).length > 0"
              >
                <TeamMemberPathway
                  :model="pathwayProgress(member.pathways.currentPathwaysInProgress, true)[0]"
                  :isCurrentPathway="true"
                ></TeamMemberPathway>
              </template>
              <span v-else class="text-muted" data-test="no-pathway-progress">no progress</span>
              <TeamMemberPathway
                v-for="(otherProgress, index) in pathwayProgress(
                  member.pathways.otherProgress,
                  false,
                )"
                :key="index"
                :model="otherProgress"
                :isCurrentPathway="false"
                class="hidden-on-collapse"
                data-test="other-pathway-progress"
              />
            </td>
          </tr>
          <tr v-for="pending in model.pendingMembers" :key="pending.userId">
            <td></td>
            <td></td>
            <td>
              <span v-if="!!pending.firstName">{{ pending.firstName }}</span>
              <a v-else :href="'mailto:' + pending.email">{{ pending.email }}</a>
            </td>
            <td style="min-width: 150px">
              <span v-if="!!pending.lastName">{{ pending.lastName }}</span>
              <span v-else style="display: none">ZZZZ</span>
            </td>
            <td></td>

            <td></td>

            <td>
              <span class="text-muted">(pending...)</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Tooltip } from "uiv";
import { triggerRef, ref } from "vue";
import TeamMemberReportLink from "./team-member-report-link.vue";
import TeamMemberDevelopLine from "./team-member-develop-line.vue";
import {
  TeamCurrentAndPendingMembersApiModel,
  TeamCurrentMemberApiModel,
  TeamMemberCompletedPathwayStepApiModel,
} from "../../api-models";
import TeamMemberPathway from "./team-member-pathway.vue";
import { csContext } from "../../services/cs-context";

const props = defineProps<{
  model: TeamCurrentAndPendingMembersApiModel;
  teamId: string;
}>();

const expanded = ref<Record<string, boolean>>({});

const userId = csContext.user.id;

const isExpanded = (member: TeamCurrentMemberApiModel): boolean => {
  return !!expanded.value[member.userDetails.email];
};

const memberFullName = (member: TeamCurrentMemberApiModel): string => {
  return `${member.userDetails.firstName} ${member.userDetails.lastName}`;
};

const memberProfileCanBeViewed = (member: TeamCurrentMemberApiModel): boolean => {
  return userId === member.userDetails.id || member.isUserProfileSharedWithOrganisation;
};

const toggleMemberExpansion = (member: TeamCurrentMemberApiModel): void => {
  if (typeof expanded.value[member.userDetails.email] === "undefined") {
    expanded.value[member.userDetails.email] = false;
  }
  expanded.value[member.userDetails.email] = !expanded.value[member.userDetails.email];
  triggerRef(expanded);
};

const developDay = (member: TeamCurrentMemberApiModel): number => {
  return member.develop.currentStrengthProgress?.lastDayIndex ?? 0;
};

const pathwayProgress = (
  progress: TeamMemberCompletedPathwayStepApiModel[],
  isCurrent: boolean,
) => {
  if (isCurrent) {
    return progress.filter((p) => p.teamId === props.teamId && !p.teamPathwayIsComplete);
  }
  return progress.filter((p) => p.teamId === props.teamId);
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.expandable-row:not(.expanded) .hidden-on-collapse {
  display: none;
}

.table-padding {
  padding-left: 30px;
  @media (max-width: @screen-sm) {
    padding-left: 0;
  }
}

.leader-icon,
.leader-icon-mobile {
  height: 25px;
}

.leader-icon {
  position: absolute;
  left: -30px;
  @media (max-width: @screen-sm) {
    display: none;
  }
}

.last-name {
  @media (max-width: @screen-sm) {
    display: flex;
  }
}

.leader-icon-mobile {
  display: none;
  width: 25px;
  @media (max-width: @screen-sm) {
    display: block;
  }
}
</style>
